import React, { useEffect, useMemo, useState } from 'react';
import {
  BriefcaseBusiness,
  Building2,
  ChevronLeft,
  ChevronRight,
  Clock3,
  Filter,
  Pencil,
  Plus,
  RefreshCw,
  Search,
  Settings,
  Trash2,
  Users,
} from 'lucide-react';
import '../Employees/Employees.css';
import { getDepartments, createDepartment, updateDepartment, deleteDepartment } from '../hrisApi';
import DepartmentFormModal from './DepartmentFormModal';
import ConfirmationModal from '../../../components/common/ConfirmationModal';

const statusClass = (value) => String(value || 'Active').toLowerCase().replace(/\s+/g, '-');

const toDepartmentRow = (record, index) => {
  const positions = Array.isArray(record.positions)
    ? record.positions
    : Array.isArray(record.jobTitles)
      ? record.jobTitles
      : [];

  return {
    id: record.id || record.departmentCode || record.code || record.departmentName || `department-${index}`,
    name: record.departmentName || record.name || record.department || '—',
    code: record.departmentCode || record.code || '—',
    parent: record.parentName || record.parentDepartment || record.parent || '—',
    company: record.companyName || record.company || '—',
    manager: record.managerName || record.manager || '—',
    employeeCount: Number(record.employeeCount || record.employees || record.totalEmployees || 0),
    positionsCount: Number(record.positionsCount || record.totalPositions || positions.length || 0),
    positionsLabel: record.positionsLabel || record.jobTitleSummary || (positions.length ? positions.map((item) => item.name || item.title || item.positionName).filter(Boolean).join(', ') : '—'),
    status: record.status || 'Active',
    raw: record,
  };
};

function Status({ value }) {
  return <span className={`employee-status ${statusClass(value)}`}><i />{value}</span>;
}

const Department = () => {
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');
  const [company, setCompany] = useState('All companies');
  const [manager, setManager] = useState('All managers');
  const [status, setStatus] = useState('All statuses');
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(7);
  const [showFilters, setShowFilters] = useState(true);

  const [showFormModal, setShowFormModal] = useState(false);
  const [editingDepartment, setEditingDepartment] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deletingDepartment, setDeletingDepartment] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [actionError, setActionError] = useState('');

  const load = () => {
    setLoading(true);
    setError(null);
    getDepartments({ page: 0, size: 500 })
      .then((result) => {
        const rows = (result.content || []).map(toDepartmentRow);
        setDepartments(rows);
      })
      .catch(() => setError('Could not load departments from the server.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const openAddModal = () => { setEditingDepartment(null); setActionError(''); setShowFormModal(true); };
  const openEditModal = (department) => { setEditingDepartment(department.raw); setActionError(''); setShowFormModal(true); };

  const handleSaveDepartment = async (formValues) => {
    setSaving(true);
    try {
      if (editingDepartment) {
        await updateDepartment(editingDepartment.id, formValues);
      } else {
        await createDepartment(formValues);
      }
      setShowFormModal(false);
      load();
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteDepartment = async () => {
    if (!deletingDepartment) return;
    setDeleting(true);
    setActionError('');
    try {
      await deleteDepartment(deletingDepartment.raw.id);
      setDeletingDepartment(null);
      load();
    } catch (err) {
      setActionError(err?.response?.data?.message || err?.message || 'Gagal menghapus department.');
    } finally {
      setDeleting(false);
    }
  };

  const unique = (key) => [...new Set(departments.map((item) => item[key]).filter((value) => value && value !== '—'))];

  const filtered = useMemo(() => departments.filter((item) => {
    const haystack = `${item.name} ${item.code} ${item.parent} ${item.company} ${item.manager} ${item.positionsLabel}`.toLowerCase();
    return (!query || haystack.includes(query.toLowerCase()))
      && (company === 'All companies' || item.company === company)
      && (manager === 'All managers' || item.manager === manager)
      && (status === 'All statuses' || item.status === status);
  }), [departments, query, company, manager, status]);

  const visible = filtered.slice((page - 1) * rowsPerPage, page * rowsPerPage);
  const totalPages = Math.max(1, Math.ceil(filtered.length / rowsPerPage));
  const activeFilters = [company !== 'All companies', manager !== 'All managers', status !== 'All statuses'].filter(Boolean).length;
  const totalEmployees = departments.reduce((sum, item) => sum + item.employeeCount, 0);
  const totalPositions = departments.reduce((sum, item) => sum + item.positionsCount, 0);

  const reset = () => {
    setQuery('');
    setCompany('All companies');
    setManager('All managers');
    setStatus('All statuses');
    setPage(1);
  };

  const select = (event, setter) => {
    setter(event.target.value);
    setPage(1);
  };

  const changeRowsPerPage = (event) => {
    setRowsPerPage(Number(event.target.value));
    setPage(1);
  };

  return (
    <div className="employee-directory-page hris-master-page">
      <header className="employee-page-header">
        <div>
          <div className="eyebrow"><Settings size={15} /> HUMAN RESOURCES</div>
          <h1>Department</h1>
          <p>Directory department untuk melihat struktur organisasi, manager, job title, dan jumlah karyawan.</p>
        </div>
        <div className="d-flex align-items-center gap-3">
          <div className="source-state">
            <span><i /> {loading ? 'Loading…' : error ? 'Offline' : 'Live data'}</span>
            <small>{error || 'Synced from HRIS backend'}</small>
          </div>
          <button type="button" className="btn btn-primary d-flex align-items-center gap-1" onClick={openAddModal}><Plus size={16} /> Add Department</button>
        </div>
      </header>

      {actionError && <div className="alert alert-danger">{actionError}</div>}

      <section className="employee-summary-grid">
        <article><span className="summary-icon teal"><Building2 size={21} /></span><div><small>Total departments</small><strong>{departments.length}</strong></div></article>
        <article><span className="summary-icon blue"><BriefcaseBusiness size={21} /></span><div><small>Job titles mapped</small><strong>{totalPositions}</strong></div></article>
        <article><span className="summary-icon purple"><Users size={21} /></span><div><small>Employees mapped</small><strong>{totalEmployees}</strong></div></article>
        <article><span className="summary-icon orange"><Settings size={21} /></span><div><small>Active departments</small><strong>{departments.filter((item) => item.status === 'Active').length}</strong></div></article>
      </section>

      <section className="employee-filter-panel">
        <div className="filter-panel-head">
          <div>
            <h2>Department directory</h2>
            <p>Gunakan pencarian dan filter untuk menemukan department yang sesuai.</p>
          </div>
          <button className="filter-toggle" onClick={() => setShowFilters((value) => !value)}>
            <Filter size={16} /> Filters {activeFilters > 0 && <b>{activeFilters}</b>}
          </button>
        </div>
        <div className="search-row">
          <label className="employee-search">
            <Search size={18} />
            <input
              value={query}
              onChange={(event) => { setQuery(event.target.value); setPage(1); }}
              placeholder="Search department, code, company, manager..."
            />
          </label>
          <button className="clear-filter" onClick={reset}>Clear</button>
          <button className="refresh-button" onClick={load} title="Refresh from server"><RefreshCw size={17} /> Refresh</button>
        </div>
        {showFilters && (
          <div className="filter-row">
            <label>Company<select value={company} onChange={(event) => select(event, setCompany)}><option>All companies</option>{unique('company').map((value) => <option key={value}>{value}</option>)}</select></label>
            <label>Manager<select value={manager} onChange={(event) => select(event, setManager)}><option>All managers</option>{unique('manager').map((value) => <option key={value}>{value}</option>)}</select></label>
            <label>Status<select value={status} onChange={(event) => select(event, setStatus)}><option>All statuses</option>{unique('status').map((value) => <option key={value}>{value}</option>)}</select></label>
          </div>
        )}
      </section>

      <section className="employee-table-panel">
        <div className="directory-heading">
          <div>
            <h2>Departments</h2>
            <p>{filtered.length} department{filtered.length === 1 ? '' : 's'} shown</p>
          </div>
          <span className="sync-note"><Clock3 size={15} /> Synced from HRIS backend</span>
        </div>
        <div className="table-scroll">
          <table className="employee-table department-table">
            <thead>
              <tr>
                <th>Department</th>
                <th>Company</th>
                <th>Manager</th>
                <th>Parent</th>
                <th>Job titles</th>
                <th>Employees</th>
                <th>Status</th>
                <th aria-label="Action" />
              </tr>
            </thead>
            <tbody>
              {visible.length ? visible.map((item) => (
                <tr key={item.id}>
                  <td>
                    <div className="name-cell">
                      <span className="department-avatar"><Building2 size={17} /></span>
                      <span><strong>{item.name}</strong><small>{item.code}</small></span>
                    </div>
                  </td>
                  <td>{item.company}</td>
                  <td>{item.manager}</td>
                  <td><span className="department-pill">{item.parent}</span></td>
                  <td>
                    <div className="competency-tags department-position-tags">
                      {item.positionsLabel === '—' ? <span className="more">No job title</span> : item.positionsLabel.split(',').slice(0, 3).map((value) => <span key={`${item.id}-${value.trim()}`}>{value.trim()}</span>)}
                      {item.positionsLabel !== '—' && item.positionsLabel.split(',').length > 3 && <span className="more">+{item.positionsLabel.split(',').length - 3}</span>}
                    </div>
                  </td>
                  <td><span className="assignment-count"><Users size={15} /> {item.employeeCount}</span></td>
                  <td><Status value={item.status} /></td>
                  <td className="text-nowrap">
                    <button className="btn btn-icon btn-sm btn-outline-secondary" title="Edit" onClick={() => openEditModal(item)}><Pencil size={14} /></button>{' '}
                    <button className="btn btn-icon btn-sm btn-outline-danger" title="Delete" onClick={() => { setActionError(''); setDeletingDepartment(item); }}><Trash2 size={14} /></button>
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan="8" className="empty-state">{loading ? 'Loading departments…' : 'Tidak ada department yang sesuai dengan filter.'}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <footer className="table-footer">
          <span>Showing {filtered.length ? ((page - 1) * rowsPerPage) + 1 : 0}-{Math.min(page * rowsPerPage, filtered.length)} of {filtered.length} departments</span>
          <div className="pagination-controls">
            <label>Rows per page<select value={rowsPerPage} onChange={changeRowsPerPage}><option value={5}>5</option><option value={7}>7</option><option value={10}>10</option><option value={25}>25</option></select></label>
            <div className="pager">
              <button disabled={page === 1} onClick={() => setPage((value) => value - 1)} aria-label="Previous page"><ChevronLeft size={18} /></button>
              <b>{page}</b>
              <button disabled={page === totalPages} onClick={() => setPage((value) => value + 1)} aria-label="Next page"><ChevronRight size={18} /></button>
            </div>
          </div>
        </footer>
      </section>
      <DepartmentFormModal
        isOpen={showFormModal}
        onClose={() => setShowFormModal(false)}
        onSave={handleSaveDepartment}
        department={editingDepartment}
        saving={saving}
      />
      <ConfirmationModal
        isOpen={Boolean(deletingDepartment)}
        onClose={() => setDeletingDepartment(null)}
        onConfirm={handleDeleteDepartment}
        title="Delete Department"
        message={`Hapus department "${deletingDepartment?.name || ''}" dari Datahub? Tindakan ini tidak dapat dibatalkan.`}
        confirmText="Delete"
        confirmVariant="danger"
        isLoading={deleting}
      />
    </div>
  );
};

export default Department;
